import { Badge, Checkbox } from '@mantine/core';
import {
  IconCalendar,
  IconCalendarEvent,
  IconChecklist,
  IconMessage,
  IconReceipt,
  IconTools,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import UserAvatar from 'src/Components/NavBar/UserAvatar';
import { getNextTaskStatus, TaskRelatedEntityDto, TaskStatus } from 'src/types/task.types';

import styles from './TaskItem.module.scss';
import { Props } from './TaskItem.types';

const getContextIcon = (type: TaskRelatedEntityDto['type']) => {
  switch (type) {
    case 'project':
      return <IconTools size={12} />;
    case 'event':
      return <IconCalendarEvent size={12} />;
    case 'expense':
      return <IconReceipt size={12} />;
    case 'checklist':
      return <IconChecklist size={12} />;
    case 'communityFeedItem':
      return <IconMessage size={12} />;
    default:
      return null;
  }
};

const TaskItem = ({ task, onStatusChange, onClick }: Props) => {
  const { t } = useTranslation();

  const isCompleted = task.status === TaskStatus.DONE;
  const isOverdue = task.dueDate && dayjs(task.dueDate).isBefore(dayjs(), 'day') && !isCompleted;
  const isDueSoon =
    task.dueDate &&
    dayjs(task.dueDate).diff(dayjs(), 'day') <= 1 &&
    dayjs(task.dueDate).isAfter(dayjs(), 'day');

  const handleCheckboxChange = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onStatusChange) {
      onStatusChange(task.id, isCompleted ? TaskStatus.TODO : TaskStatus.DONE);
    }
  };

  const handleStatusBadgeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onStatusChange) {
      onStatusChange(task.id, getNextTaskStatus(task.status));
    }
  };

  const getStatusBadgeColor = () => {
    switch (task.status) {
      case TaskStatus.TODO:
        return 'orange';
      case TaskStatus.IN_PROGRESS:
        return 'blue';
      case TaskStatus.DONE:
        return 'teal';
      default:
        return 'gray';
    }
  };

  const getStatusLabel = () => {
    switch (task.status) {
      case TaskStatus.TODO:
        return t('tasks.status.todo', 'À faire');
      case TaskStatus.IN_PROGRESS:
        return t('tasks.status.inProgress', 'En cours');
      case TaskStatus.DONE:
        return t('tasks.status.done', 'Terminé');
      default:
        return '';
    }
  };

  return (
    <div className={styles.container} onClick={() => onClick?.(task)}>
      <div className={styles.left}>
        <div className={styles.checkbox} onClick={handleCheckboxChange}>
          <Checkbox checked={isCompleted} readOnly size="md" />
        </div>
        <div className={styles.content}>
          <div className={`${styles.title} ${isCompleted ? styles.completed : ''}`}>
            {task.title}
          </div>
          {task.relatedEntities &&
            task.relatedEntities.length > 0 &&
            task.relatedEntities.map(entity => (
              <Badge
                key={`${entity.type}-${entity.id}`}
                size="xs"
                variant="light"
                color="violet"
                leftSection={getContextIcon(entity.type)}
              >
                {entity.title}
              </Badge>
            ))}
        </div>
      </div>
      <div className={styles.right}>
        {task.dueDate && (
          <div
            className={`${styles.dueDate} ${isOverdue ? styles.overdue : ''} ${isDueSoon ? styles.dueSoon : ''}`}
          >
            <IconCalendar size={14} />
            <span>{dayjs(task.dueDate).format('DD/MM')}</span>
          </div>
        )}
        <Badge
          size="sm"
          color={getStatusBadgeColor()}
          style={{ cursor: 'pointer' }}
          onClick={handleStatusBadgeClick}
        >
          {getStatusLabel()}
        </Badge>
        {task.assignedTo && <UserAvatar user={task.assignedTo} size="sm" />}
      </div>
    </div>
  );
};

export default TaskItem;
